/**
 * Common utility functions
 */

/**
 * Combines class names
 */
export function cn(...inputs: (string | undefined | null | boolean)[]): string {
  return inputs.filter(Boolean).join(' ');
}

type ScrollSnapState = {
  bodyScrollSnapType: string;
  rootScrollSnapType: string;
};

let savedScrollSnapState: ScrollSnapState | null = null;
let scrollSnapRestoreTimer: number | null = null;

const scrollSnapRestoreDelayMs = 1200;

function disablePageScrollSnapTemporarily() {
  if (typeof window === 'undefined') {
    return;
  }

  const root = document.documentElement;
  const body = document.body;

  if (savedScrollSnapState === null) {
    savedScrollSnapState = {
      bodyScrollSnapType: body.style.scrollSnapType,
      rootScrollSnapType: root.style.scrollSnapType
    };
  }

  if (scrollSnapRestoreTimer !== null) {
    window.clearTimeout(scrollSnapRestoreTimer);
    scrollSnapRestoreTimer = null;
  }

  root.style.scrollSnapType = 'none';
  body.style.scrollSnapType = 'none';

  scrollSnapRestoreTimer = window.setTimeout(() => {
    if (savedScrollSnapState !== null) {
      root.style.scrollSnapType = savedScrollSnapState.rootScrollSnapType;
      body.style.scrollSnapType = savedScrollSnapState.bodyScrollSnapType;
      savedScrollSnapState = null;
    }

    scrollSnapRestoreTimer = null;
  }, scrollSnapRestoreDelayMs);
}

export function scrollToElementId(id: string): boolean {
  const section = document.getElementById(id);

  if (!section) {
    return false;
  }

  const sectionTop = section.getBoundingClientRect().top + window.scrollY;
  const scrollOffset = Number(section.dataset.scrollOffset ?? 0);

  disablePageScrollSnapTemporarily();

  window.scrollTo({
    top: Math.max(sectionTop - scrollOffset, 0),
    behavior: 'smooth'
  });

  return true;
}

/**
 * Formats a date string to a readable format
 */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  }).format(dateObj);
}

/**
 * Capitalizes the first letter of a string
 */
export function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Generates a random ID string
 */
export function generateId(length = 8): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Truncates a string to a specified length
 */
export function truncate(str: string, length: number, suffix = '...'): string {
  if (str.length <= length) return str;
  return str.slice(0, length - suffix.length) + suffix;
}

/**
 * Debounces a function call
 */
export function debounce<T extends (...args: never[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttles a function call
 */
export function throttle<T extends (...args: never[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(this: unknown, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Deep clones an object
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as T;

  const cloned = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }

  return cloned;
}

/**
 * Checks if a value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object')
    return Object.keys(value as Record<string, unknown>).length === 0;
  return false;
}

/**
 * Safely accesses nested object properties
 */
export function get(
  obj: Record<string, unknown>,
  path: string,
  defaultValue?: unknown
): unknown {
  const keys = path.split('.');
  let result: unknown = obj;

  for (const key of keys) {
    if (
      result === null ||
      result === undefined ||
      typeof result !== 'object' ||
      !(key in result)
    ) {
      return defaultValue;
    }
    result = (result as Record<string, unknown>)[key];
  }

  return result;
}
